import { createContext, ReactElement, useReducer } from "react"
import usersReducer, { initialUsersState } from "../store/reducers/users"
import axios from "utils/axios"
import {
  FETCH_ANALYTICS_USER_LOGS,
  FETCH_CLICKUP_USERS,
  FETCH_SCOUT_USERS,
  FETCH_USERS
} from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToCamelCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { getFilterText, toastMessage } from "../utils"
import { User, UsersContextType } from "../types/users"

const UsersContext = createContext<UsersContextType | null>(null)

export const UsersProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(usersReducer, initialUsersState)

  const fetchUsers = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const response = await axios(
      `/users/?limit=${limit}&offset=${offset}${getFilterText(filter)}${orderBy ? `&order_by=${orderBy}` : ""}`,
      { method: "get" }
    )

    dispatch({
      type: FETCH_USERS,
      payload: { ...state, paginatedUsers: mapPaginatedDataToCamelCase<PaginatedData<User>>(response.data) }
    })
  }

  const fetchClickupUsers = async () => {
    const response = await axios(`/users/clickup_users/`, { method: "get" })

    dispatch({
      type: FETCH_CLICKUP_USERS,
      payload: { ...state, clickupUsers: mapPaginatedDataToCamelCase<PaginatedData<User>>(response.data) }
    })
  }

  const fetchScoutUsers = async () => {
    const response = await axios(`/users/scout_users/`, { method: "get" })

    dispatch({
      type: FETCH_SCOUT_USERS,
      payload: { ...state, scoutUsers: mapPaginatedDataToCamelCase<PaginatedData<User>>(response.data) }
    })
  }

  const updateRole = async (userId: number, roleId: number, value: boolean) => {
    const response = await axios(`/users/update_role/`, {
      method: "put",
      data: { user_id: userId, role_id: roleId, value }
    })

    if (!response.data.success) {
      toastMessage("Error saving role", "error")
      dispatch({
        type: FETCH_USERS,
        payload: { ...state }
      })
      return
    }

    const updatedResults = [
      ...state.paginatedUsers.results.map((result) => {
        if (result.id === userId) {
          if (value) {
            result.roleIds = [...(result.roleIds || []), roleId]
          } else {
            result.roleIds = [...(result.roleIds || []).filter((id) => roleId !== id)]
          }
        }
        return result
      })
    ]

    toastMessage("Success", "success")
    dispatch({
      type: FETCH_USERS,
      payload: { ...state, paginatedUsers: { ...state.paginatedUsers, results: [...updatedResults] } }
    })
  }

  const updateClickupStatus = async (userId: number, value: boolean) => {
    const response = await axios(`/users/save_clickup/`, {
      method: "put",
      data: { user_id: userId, checked: value }
    })

    if (!response.data.success) {
      toastMessage("Error saving mention user", "error")
      dispatch({ type: FETCH_USERS, payload: { ...state } })
      return
    }

    const updatedResults = [
      ...state.paginatedUsers.results.map((result) => {
        if (result.id === userId) {
          result.clickupId = value ? `kah_${userId}` : undefined
        }
        return result
      })
    ]

    toastMessage("Success", "success")
    dispatch({
      type: FETCH_USERS,
      payload: { ...state, paginatedUsers: { ...state.paginatedUsers, results: [...updatedResults] } }
    })
  }

  const updateOutreachTarget = async (userId: number, value: number) => {
    const response = await axios(`/users/update_outreach_target/`, {
      method: "put",
      data: { user_id: userId, outreach_target: value }
    })

    if (!response.data.success) {
      toastMessage("Error saving outreach target", "error")
      dispatch({
        type: FETCH_USERS,
        payload: { ...state }
      })
      return
    }

    const updatedResults = [
      ...state.paginatedUsers.results.map((result) => {
        if (result.id === userId) {
          result.outreachTarget = value
        }
        return result
      })
    ]

    toastMessage("Success", "success")
    dispatch({
      type: FETCH_USERS,
      payload: { ...state, paginatedUsers: { ...state.paginatedUsers, results: [...updatedResults] } }
    })
  }

  const fetchAnalyticsUserLogs = async (limit: number, offset: number, filter?: any, orderBy?: string) => {
    const response = await axios(
      `/user-logs/analytics/?limit=${limit}&offset=${offset}${getFilterText(filter)}${
        orderBy ? `&order_by=${orderBy}` : ""
      }`,
      { method: "get" }
    )

    dispatch({
      type: FETCH_ANALYTICS_USER_LOGS,
      payload: {
        ...state,
        analyticsUserLogs: mapPaginatedDataToCamelCase<PaginatedData<User>>(response.data)
      }
    })
  }

  return (
    <UsersContext.Provider
      value={{
        ...state,
        fetchUsers,
        updateRole,
        updateClickupStatus,
        fetchClickupUsers,
        fetchScoutUsers,
        fetchAnalyticsUserLogs,
        updateOutreachTarget
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}

export default UsersContext
